/**
 * Plan entitlements: one table for what every plan includes.
 *
 * Replaces the substring matching (`includes("brand")`, `includes("agencycore")`)
 * that `getIncludedPrompts` and friends used to do. Under that matching a new
 * key like `agencyPro` silently got 0 prompts, and `brandV2` only worked by
 * accident. Every plan key in config now has an explicit row, enforced by the
 * `Record<PlanKey, …>` type.
 *
 * Browser-safe (exported from `@repo/payments/client`): no server imports, and
 * only a type import from `@repo/config`.
 *
 * The per-plan allowance constants live here, not in `credit-costs.ts`, so this
 * module has no imports of its own and `credit-costs.ts` can wrap it without a
 * module cycle. `credit-costs.ts` re-exports every one of them under the same
 * name, so existing imports keep working.
 */
import type { PlanKey } from "@repo/config";

// ============================================================================
// v1 ALLOWANCES (frozen: existing Brand $29 / Agency Core $199 subscribers)
// ============================================================================

/**
 * Brand plan: included prompts
 * Default: 25 prompts
 */
export const BRAND_INCLUDED_PROMPTS = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_INCLUDED_PROMPTS || "25",
	10,
);

/**
 * Agency Core plan: included prompts
 * Default: 250 prompts
 */
export const AGENCY_CORE_INCLUDED_PROMPTS = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_INCLUDED_PROMPTS || "250",
	10,
);

/**
 * Brand plan: monthly site audit page allocation
 * Default: 250 pages/month
 */
export const BRAND_SITE_AUDIT_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_SITE_AUDIT_PAGES_PER_MONTH || "250",
	10,
);

/**
 * Agency Core plan: monthly site audit page allocation
 * These pages are "pure" — each page audited deducts one from the pool.
 * Default: 2500 pages/month
 */
export const AGENCY_CORE_SITE_AUDIT_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_SITE_AUDIT_PAGES_PER_MONTH || "2500",
	10,
);

/**
 * Brand plan: monthly included page rewrites.
 * Default: 10 rewrites/month
 */
export const BRAND_CITATION_REWRITES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_CITATION_REWRITES_PER_MONTH || "10",
	10,
);

/**
 * Agency Core plan: monthly included page rewrites.
 * Default: 50 rewrites/month
 */
export const AGENCY_CORE_CITATION_REWRITES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_CITATION_REWRITES_PER_MONTH || "50",
	10,
);

/**
 * Free / trial / pay-as-you-go without a subscription: monthly scored pages.
 * Default: 5 pages/month — keeps the acquisition surface at roughly 2¢ a page.
 */
export const FREE_CITATION_SCORING_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_FREE_CITATION_SCORING_PAGES_PER_MONTH || "5",
	10,
);

/**
 * Brand plan: monthly scored pages.
 * Default: 50 pages/month
 */
export const BRAND_CITATION_SCORING_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_CITATION_SCORING_PAGES_PER_MONTH || "50",
	10,
);

/**
 * Agency Core plan: monthly scored pages.
 * Default: 250 pages/month
 */
export const AGENCY_CORE_CITATION_SCORING_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_CITATION_SCORING_PAGES_PER_MONTH ||
		"250",
	10,
);

// ============================================================================
// v2 ALLOWANCES (October 2026 plans)
// ============================================================================

/** Brand v2 ($79): included prompts. Default: 50 */
export const BRAND_V2_INCLUDED_PROMPTS = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_V2_INCLUDED_PROMPTS || "50",
	10,
);

/** Brand v2: monthly site audit pages. Default: 250 */
export const BRAND_V2_SITE_AUDIT_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_V2_SITE_AUDIT_PAGES_PER_MONTH || "250",
	10,
);

/** Brand v2: monthly included page rewrites. Default: 10 */
export const BRAND_V2_CITATION_REWRITES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_V2_CITATION_REWRITES_PER_MONTH || "10",
	10,
);

/** Brand v2: monthly scored pages. Default: 50 */
export const BRAND_V2_CITATION_SCORING_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_BRAND_V2_CITATION_SCORING_PAGES_PER_MONTH || "50",
	10,
);

/** Agency Core v2 ($349): included prompts. Default: 250 */
export const AGENCY_CORE_V2_INCLUDED_PROMPTS = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_V2_INCLUDED_PROMPTS || "250",
	10,
);

/** Agency Core v2: monthly site audit pages. Default: 2,500 */
export const AGENCY_CORE_V2_SITE_AUDIT_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_V2_SITE_AUDIT_PAGES_PER_MONTH || "2500",
	10,
);

/** Agency Core v2: monthly included page rewrites. Default: 50 */
export const AGENCY_CORE_V2_CITATION_REWRITES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_V2_CITATION_REWRITES_PER_MONTH || "50",
	10,
);

/** Agency Core v2: monthly scored pages. Default: 250 */
export const AGENCY_CORE_V2_CITATION_SCORING_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_CORE_V2_CITATION_SCORING_PAGES_PER_MONTH ||
		"250",
	10,
);

/** Agency Pro ($699): included prompts. Default: 600 */
export const AGENCY_PRO_INCLUDED_PROMPTS = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_PRO_INCLUDED_PROMPTS || "600",
	10,
);

/** Agency Pro: monthly site audit pages. Default: 6,000 */
export const AGENCY_PRO_SITE_AUDIT_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_PRO_SITE_AUDIT_PAGES_PER_MONTH || "6000",
	10,
);

/** Agency Pro: monthly included page rewrites. Default: 120 */
export const AGENCY_PRO_CITATION_REWRITES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_PRO_CITATION_REWRITES_PER_MONTH || "120",
	10,
);

/** Agency Pro: monthly scored pages. Default: 600 */
export const AGENCY_PRO_CITATION_SCORING_PAGES_PER_MONTH = Number.parseInt(
	process.env.NEXT_PUBLIC_AGENCY_PRO_CITATION_SCORING_PAGES_PER_MONTH ||
		"600",
	10,
);

// ============================================================================
// ENTITLEMENTS TABLE
// ============================================================================

/**
 * The base platforms every tracked prompt runs on, daily, at no add-on cost.
 * Plain strings: `@repo/payments` must not import `@repo/core`. Claude and
 * Perplexity are add-ons (see `add-ons.ts`).
 */
export const BASE_TRACKING_PLATFORMS = [
	"chatgpt",
	"gemini",
	"google_ai_overview",
] as const;

export type PlanEntitlementCategory =
	| "free"
	| "brand"
	| "agency"
	| "enterprise";

export type CitationPlanBucket = "free" | "brand" | "agency" | "enterprise";

export interface PlanEntitlements {
	/** "free" covers PAYG, lifetime and no-plan orgs. */
	category: PlanEntitlementCategory;
	/** Bundled daily-tracked prompts. Infinity for enterprise. */
	trackedPrompts: number;
	tracking: {
		cadence: "daily";
		platforms: readonly (typeof BASE_TRACKING_PLATFORMS)[number][];
	};
	/**
	 * Bundled credits per month. Must equal `config.payments.plans[k].credits`
	 * for every plan with credits (asserted in plan-entitlements.test.ts); the
	 * webhooks and the vesting cron grant from config.
	 */
	monthlyCredits: number;
	/** Site-audit pages per month. Infinity for enterprise. */
	siteAuditPages: number;
	/** Included citation rewrites per month. Infinity for enterprise. */
	citationRewrites: number;
	/** Scored pages per month (free plans get the small meter). */
	citationScoringPages: number;
	citationBucket: CitationPlanBucket;
	/**
	 * Placeholder for the broad-audit axis (AI Visibility report sweeps). Not
	 * enforced anywhere yet.
	 */
	auditSweeps: { aiVisibilityReportsPerMonth: number };
}

const DAILY_BASE_TRACKING: PlanEntitlements["tracking"] = {
	cadence: "daily",
	platforms: BASE_TRACKING_PLATFORMS,
};

const NO_SWEEPS: PlanEntitlements["auditSweeps"] = {
	aiVisibilityReportsPerMonth: 0,
};

/** What an org with no subscription plan gets (PAYG, lifetime, none). */
export const FREE_ENTITLEMENTS: PlanEntitlements = {
	category: "free",
	trackedPrompts: 0,
	tracking: DAILY_BASE_TRACKING,
	monthlyCredits: 0,
	siteAuditPages: 0,
	citationRewrites: 0,
	citationScoringPages: FREE_CITATION_SCORING_PAGES_PER_MONTH,
	citationBucket: "free",
	auditSweeps: NO_SWEEPS,
};

const ENTERPRISE_ENTITLEMENTS: PlanEntitlements = {
	category: "enterprise",
	trackedPrompts: Number.POSITIVE_INFINITY,
	tracking: DAILY_BASE_TRACKING,
	monthlyCredits: 0,
	siteAuditPages: Number.POSITIVE_INFINITY,
	citationRewrites: Number.POSITIVE_INFINITY,
	citationScoringPages: Number.POSITIVE_INFINITY,
	citationBucket: "enterprise",
	auditSweeps: NO_SWEEPS,
};

/** v1 Brand ($29). Frozen. */
const BRAND_V1_ENTITLEMENTS: PlanEntitlements = {
	category: "brand",
	trackedPrompts: BRAND_INCLUDED_PROMPTS,
	tracking: DAILY_BASE_TRACKING,
	monthlyCredits: 30,
	siteAuditPages: BRAND_SITE_AUDIT_PAGES_PER_MONTH,
	citationRewrites: BRAND_CITATION_REWRITES_PER_MONTH,
	citationScoringPages: BRAND_CITATION_SCORING_PAGES_PER_MONTH,
	citationBucket: "brand",
	auditSweeps: NO_SWEEPS,
};

/** v1 Agency Core allowances, shared by `agencyCore` and `agencyCoreUsageBased`. */
function agencyCoreV1Entitlements(monthlyCredits: number): PlanEntitlements {
	return {
		category: "agency",
		trackedPrompts: AGENCY_CORE_INCLUDED_PROMPTS,
		tracking: DAILY_BASE_TRACKING,
		monthlyCredits,
		siteAuditPages: AGENCY_CORE_SITE_AUDIT_PAGES_PER_MONTH,
		citationRewrites: AGENCY_CORE_CITATION_REWRITES_PER_MONTH,
		citationScoringPages: AGENCY_CORE_CITATION_SCORING_PAGES_PER_MONTH,
		citationBucket: "agency",
		auditSweeps: NO_SWEEPS,
	};
}

export const PLAN_ENTITLEMENTS: Record<PlanKey, PlanEntitlements> = {
	lifetime: FREE_ENTITLEMENTS,
	paygCredits: FREE_ENTITLEMENTS,
	aiReportUnlock: FREE_ENTITLEMENTS,
	enterprise: ENTERPRISE_ENTITLEMENTS,
	// Legacy bundled-credit agency plan ($499).
	agencyCore: agencyCoreV1Entitlements(350),
	brand: BRAND_V1_ENTITLEMENTS,
	agencyCoreUsageBased: agencyCoreV1Entitlements(300),
	brandV2: {
		category: "brand",
		trackedPrompts: BRAND_V2_INCLUDED_PROMPTS,
		tracking: DAILY_BASE_TRACKING,
		monthlyCredits: 60,
		siteAuditPages: BRAND_V2_SITE_AUDIT_PAGES_PER_MONTH,
		citationRewrites: BRAND_V2_CITATION_REWRITES_PER_MONTH,
		citationScoringPages: BRAND_V2_CITATION_SCORING_PAGES_PER_MONTH,
		citationBucket: "brand",
		auditSweeps: NO_SWEEPS,
	},
	agencyCoreV2: {
		category: "agency",
		trackedPrompts: AGENCY_CORE_V2_INCLUDED_PROMPTS,
		tracking: DAILY_BASE_TRACKING,
		monthlyCredits: 300,
		siteAuditPages: AGENCY_CORE_V2_SITE_AUDIT_PAGES_PER_MONTH,
		citationRewrites: AGENCY_CORE_V2_CITATION_REWRITES_PER_MONTH,
		citationScoringPages: AGENCY_CORE_V2_CITATION_SCORING_PAGES_PER_MONTH,
		citationBucket: "agency",
		auditSweeps: NO_SWEEPS,
	},
	agencyPro: {
		category: "agency",
		trackedPrompts: AGENCY_PRO_INCLUDED_PROMPTS,
		tracking: DAILY_BASE_TRACKING,
		monthlyCredits: 720,
		siteAuditPages: AGENCY_PRO_SITE_AUDIT_PAGES_PER_MONTH,
		citationRewrites: AGENCY_PRO_CITATION_REWRITES_PER_MONTH,
		citationScoringPages: AGENCY_PRO_CITATION_SCORING_PAGES_PER_MONTH,
		citationBucket: "agency",
		auditSweeps: NO_SWEEPS,
	},
};

/**
 * Plan-type strings stored on organizations that aren't config keys but that
 * existing code has always accepted.
 */
const PLAN_TYPE_ALIASES: Record<string, PlanKey> = {
	agency_core: "agencyCore",
	payg: "paygCredits",
	free: "paygCredits",
};

/** Lowercased plan key → canonical key, for case-insensitive lookups. */
const LOWERCASE_PLAN_KEYS = new Map<string, PlanKey>(
	(Object.keys(PLAN_ENTITLEMENTS) as PlanKey[]).map((key) => [
		key.toLowerCase(),
		key,
	]),
);

/**
 * Resolve a stored `subscriptionPlanType` to its canonical plan key, or
 * `undefined` when it isn't a known key or alias. Case-insensitive, because
 * the substring matching this replaces was.
 */
export function resolvePlanKey(
	planType: string | null | undefined,
): PlanKey | undefined {
	if (!planType) return undefined;
	if (Object.prototype.hasOwnProperty.call(PLAN_ENTITLEMENTS, planType)) {
		return planType as PlanKey;
	}
	const lower = planType.toLowerCase();
	return LOWERCASE_PLAN_KEYS.get(lower) ?? PLAN_TYPE_ALIASES[lower];
}

const warnedUnknownPlanTypes = new Set<string>();

/**
 * The pre-table substring matching, kept as the fallback for plan-type strings
 * the table doesn't know so nothing that resolved before resolves differently
 * now. Each unknown value warns once per process.
 */
function legacySubstringEntitlements(planType: string): PlanEntitlements {
	if (!warnedUnknownPlanTypes.has(planType)) {
		warnedUnknownPlanTypes.add(planType);
		console.warn(
			`[plan-entitlements] Unknown plan type "${planType}"; falling back to substring matching. Add it to PLAN_ENTITLEMENTS or PLAN_TYPE_ALIASES.`,
		);
	}
	const lower = planType.toLowerCase();
	if (lower.includes("brand")) return BRAND_V1_ENTITLEMENTS;
	if (lower.includes("agencycore")) return agencyCoreV1Entitlements(0);
	return FREE_ENTITLEMENTS;
}

/**
 * What a plan includes. Null/empty plan types get the free entitlements; known
 * keys and aliases resolve through `PLAN_ENTITLEMENTS`; anything else falls
 * back to the legacy substring matching (and logs).
 */
export function getEntitlements(
	planType: string | null | undefined,
): PlanEntitlements {
	if (!planType) return FREE_ENTITLEMENTS;
	const key = resolvePlanKey(planType);
	if (key) return PLAN_ENTITLEMENTS[key];
	return legacySubstringEntitlements(planType);
}
